<template>
  <InfiniteScroll
    class="character-list"
    :status="loadStatus"
    :empty="false"
    @loadMore="next"
  >
    <Resource
      class="absolute left-0 top-0 w-full"
      :src="bgUrl"
      fetchpriority="hight"
      :lazy="false"
    />
    <div class="search-area" :style="isSticky ? {} : { background: 'none' }">
      <div class="flex justify-between w-[1200px] mx-auto">
        <ButtonSwitch
          v-model:value="searchFrom.sort"
          :options="sortOptions"
          @change="refresh"
        />
        <div>
          <a-select
            v-for="tag in characterTag?.tags"
            :key="tag.tag_id"
            v-model:value="searchFrom.tags[tag.tag_id]"
            size="small"
            :dropdown-match-select-width="false"
            class="dict-select"
            :placeholder="tag.tag_name"
            allow-clear
            :options="
              tag.children?.map((child) => ({
                value: child.tag_id,
                label: child.tag_name,
              }))
            "
            @change="refresh()"
          >
            <template #suffixIcon>
              <a-divider type="vertical" class="border-back-6 mt-[3px]" />
              <iconpark-icon
                name="arrowdown"
                class="text-font-1"
              ></iconpark-icon>
            </template>
          </a-select>
        </div>
      </div>
    </div>
    <div class="w-[1200px] mx-auto" :loadMore="next" :status="loadStatus">
      <div class="grid grid-cols-4 gap-x-[16px] gap-y-[30px]">
        <ContentCharacterCard
          v-for="digitalHuman in calList"
          :key="digitalHuman.id"
          :data="digitalHuman"
          class="h-[366px]"
          target="_blank"
          @change="onChange($event, digitalHuman)"
        >
        </ContentCharacterCard>
      </div>
      <div
        v-if="loadStatus === 'noData'"
        class="flex flex-col items-center mx-auto mt-[90px] mb-[200px] w-[421px]"
      >
        <div class="text-center">
          <Resource
            :src="getFeUrl('/youyan-platform/png/no-search-data.png')"
            :transform-resource="transformImage"
            class="w-[120px] h-[120px]"
          />
        </div>
      </div>
    </div>
  </InfiniteScroll>
</template>
<script lang="ts" setup>
import type { Character } from '~/api/interface/character'
import { getCharacterList } from '~/api/module/character'
import { TEMPLATE_SORT } from '~/constants'
import { SCROLL_LIST, useScrollList } from '~/hooks/useScrollList'
import { useStarPatchList } from '~/hooks/useStarPatch'
import { get3DHotWord } from '~/api/module/search'
import type { Tag } from '~/api/interface'
import { useWatchPageSeoData } from '~/hooks/useSeo'
import { PageKey } from '~/constants/page'

const { $t } = useNuxtApp()

provideResourceLocation({
  source: SOURCE.CHARACTER,
})

const route = useRoute()

const keyword = ref<string>((route.query.keyword as unknown as string) || '')
const characterTag = useCharacterTags()

const searchFrom = reactive({
  sort: TEMPLATE_SORT.ALL,
  tags: {} as Record<string, number>,
})

const industryTag = computed(() =>
  characterTag.value.tags.find((tag) => tag.outer_id === INDUSTRY_OUTER_ID)
)

const sceneTag = computed(() =>
  characterTag.value.tags.find((tag) => tag.outer_id === SCENE_OUTER_ID)
)

const SCENE_OUTER_ID = 'character_application'

const INDUSTRY_OUTER_ID = 'character_industry'

const industryReg = /hy(\d+)/
const sceneReg = /cj(\d+)/

const industryScene = computed(() => {
  const params = route.params.industryScene as string
  if (params) {
    const industryStr = params.match(industryReg)?.[1]
    const sceneStr = params.match(sceneReg)?.[1]
    let industry: Tag | undefined
    let scene: Tag | undefined
    if (industryStr) {
      industry = industryTag.value?.children?.find(
        (child) => child.tag_id === Number(industryStr)
      )
    }

    if (sceneStr) {
      scene = sceneTag.value?.children?.find(
        (child) => child.tag_id === Number(sceneStr)
      )
    }

    return [industry, scene]
  }
})

watch(
  () => industryScene.value,
  (val) => {
    if (val && !val[0] && !val[1]) {
      navigateTo({ name: PageKey.NOT_FOUND })
    }
  },
  { immediate: true, flush: 'sync' }
)

watchEffect(
  () => {
    const tagIds = (route.query.tag_ids || '') as string
    const tagIdArr = tagIds.split(',')
    tagIdArr.forEach((tagId) => {
      const [parentId, childId] = tagId.split('.')

      if (parentId && childId) {
        searchFrom.tags[parentId] = Number(childId)
      }
    })

    if (industryScene.value?.[0]) {
      searchFrom.tags[industryScene.value[0].pid!] =
        industryScene.value[0].tag_id
    }

    if (industryScene.value?.[1]) {
      searchFrom.tags[industryScene.value[1].pid!] =
        industryScene.value[1].tag_id
    }
  },
  { flush: 'sync' } // 避免后面接口获取的时候，tag_ids还没更新
)

// const HOT_SIZE = 8

const sortOptions = useDict().sorts.map((item) => ({
  value: item.key,
  label: item.label,
}))

// const { data: popularList, refresh: popularRefresh } = useAsyncData(() =>
//   get3DHotWord(HOT_SIZE)
// )

const isSticky = ref(true)

// function onScroll(e: Event) {
//   const scrollTop = (e.target as HTMLElement).scrollTop
//   if (scrollTop >= 0) {
//     isSticky.value = true
//   } else {
//     isSticky.value = false
//   }
// }

const getTagIds = (excludeIndustryAndScene: boolean) => {
  const tagIds: string[] = []

  Object.keys(searchFrom.tags).forEach((key) => {
    if (excludeIndustryAndScene) {
      if (
        [industryTag.value?.tag_id, sceneTag.value?.tag_id].includes(
          Number(key)
        )
      ) {
        return
      }
    }

    if (searchFrom.tags[key]) {
      tagIds.push(`${key}.${searchFrom.tags[key]}`)
    }
  })

  return tagIds.join(',')
}

const router = useRouter()

function changePath() {
  const arr: string[] = []

  let path = '/characters'

  if (industryTag.value) {
    const childId = searchFrom.tags[industryTag.value.tag_id]
    const childTag = industryTag.value.children?.find(
      (child) => child.tag_id === childId
    )

    if (childTag) {
      arr.push(`hy${childTag.tag_id}`)
    }
  }

  if (sceneTag.value) {
    const childId = searchFrom.tags[sceneTag.value.tag_id]
    const childTag = sceneTag.value.children?.find(
      (child) => child.tag_id === childId
    )

    if (childTag) {
      arr.push(`cj${childTag.tag_id}`)
    }
  }

  if (arr.length) {
    path += `/${arr.join('')}`
  }

  const query = {
    keyword: keyword.value || undefined,
    tag_ids: getTagIds(true) || undefined,
  }

  router.replace({
    path,
    query,
  })
}

const { list, refresh, pending, next, total } = useScrollList(
  async (params) => {
    const tag_ids = getTagIds(false)

    const res = await getCharacterList({
      group_id: 1,
      page: params.page,
      page_size: params.page_size,
      many: 1,
      intersection_tag_ids: tag_ids,
      o: `-${searchFrom.sort}`,
      query_star: import.meta.client,
      keyword: keyword.value,
    })

    deepOmit(res, ['look'])

    res.data?.results?.forEach((item) => {
      item.query_star = import.meta.client // 标记这些数据是否查询过star
    })

    if (import.meta.client) {
      changePath()
    }

    return res
  },
  {
    key: SCROLL_LIST.CHARACTER,
    pageSize: 20,
  }
)

const { calList, status } = useStarPatchList(list, 2)

const loadStatus = computed(() => {
  if (total.value === 0) {
    return 'noData'
  }

  if (list.value.length >= total.value) {
    return 'noMore'
  }

  if (pending.value) {
    return 'loading'
  }

  if (status.value === 'waiting') {
    return 'loading'
  }
  if (status.value === 'error') {
    return 'loadComplete'
  }
  if (status.value === 'success') {
    return 'loadComplete'
  }

  return 'loadComplete'
})

const handleOnSearch = (val: string) => {
  keyword.value = val
  refresh()
}

const { asyncBind } = useBackToTop(() => ({
  scrollContainer: '.infiniteScroll',
  item: '.character-card',
  total: total.value,
}))

watch(
  () => total.value,
  () => asyncBind()
)

const onChange = ([key, value]: [string, Character], data: Character) => {
  const item: any = calList.value?.find((item) => item.id === data.id)
  if (item) {
    item[key] = value
  }
}

const bgUrl = transformImage(
  getFeUrl('/youyan-platform/png/digital-human-list-bg.png')
)
const bgUrlCss = `url(${bgUrl})`

const seoData = computed(() => {
  let seo_title = ''
  let seo_description = ''
  let seo_keywords = ''
  if (
    industryScene.value?.[1]?.outer_id === 'character_education_and_teaching' &&
    isChinese()
  ) {
    seo_title = $t('character-list-scenarios-edu-seo-title', [
      industryScene.value[1].tag_name,
    ])
    seo_description = $t('character-list-scenarios-edu-seo-description', [
      industryScene.value[1].tag_name,
    ])
    seo_keywords = $t('character-list-scenarios-edu-seo-keywords', [
      industryScene.value[1].tag_name,
    ])
    if (industryScene.value?.[0]) {
      seo_title = $t('character-list-industry-scenarios-edu-seo-title', [
        industryScene.value[0].tag_name,
        industryScene.value[1].tag_name,
      ])
      seo_description = $t(
        'character-list-industry-scenarios-edu-seo-description',
        [industryScene.value[0].tag_name, industryScene.value[1].tag_name]
      )
    }
  } else if (industryScene.value?.[1] && industryScene.value[0]) {
    seo_title = $t('character-list-industry-scenarios-seo-title', [
      industryScene.value[0].tag_name,
      industryScene.value[1].tag_name,
    ])
    seo_description = $t('character-list-industry-scenarios-seo-description', [
      industryScene.value[0].tag_name,
      industryScene.value[1].tag_name,
    ])
    seo_keywords = $t('character-list-industry-scenarios-seo-keywords', [
      industryScene.value[0].tag_name,
      industryScene.value[1].tag_name,
    ])
  } else if (industryScene.value?.[1] && !industryScene.value[0]) {
    seo_title = $t('character-list-scenarios-seo-title', [
      industryScene.value[1].tag_name,
    ])
    seo_description = $t('character-list-scenarios-seo-description', [
      industryScene.value[1].tag_name,
    ])
    seo_keywords = $t('character-list-scenarios-seo-keywords', [
      industryScene.value[1].tag_name,
    ])
  } else if (!industryScene.value?.[1] && industryScene.value?.[0]) {
    seo_title = $t('character-list-industry-seo-title', [
      industryScene.value[0].tag_name,
    ])
    seo_description = $t('character-list-industry-seo-description', [
      industryScene.value[0].tag_name,
    ])
    seo_keywords = $t('character-list-industry-seo-keywords', [
      industryScene.value[0].tag_name,
    ])
  } else {
    seo_title = $t('character-list-seo-title', [])
    seo_description = $t('character-list-seo-description', [])
    seo_keywords = $t('character-list-seo-keywords', [])
  }

  return {
    seo_title: seo_title.trim(),
    seo_description: seo_description.trim(),
    seo_keywords: seo_description.trim(),
  }
})

useWatchPageSeoData(seoData)
</script>
<style lang="scss" scoped>
.character-list {
  height: calc(100vh - 60px);
  overflow-y: scroll;
  position: relative;
  // background: v-bind(bgUrl);
  // background-size: 100%;
  // background-repeat: no-repeat;
}

.top-area {
  // @apply sticky top-0 z-10;
  // background: v-bind(bgUrl);
  // background-size: 100%;
  // background-repeat: no-repeat;
  padding-top: 90px;
  padding-bottom: 70px;
  // background-color: var(--back-0);
  // padding-bottom: 15px;
}

.search-area {
  @apply py-[30px] sticky top-0 z-[8];
  background: v-bind(bgUrlCss);
  background-size: 100%;
  background-position: left top;
  background-repeat: no-repeat;
  background-color: var(--back-bg);
}

.dict-select {
  height: 36px;
  max-width: 130px;

  & + & {
    margin-left: 16px;
  }

  :deep(.ant-select-selector) {
    height: 36px !important;
    border-radius: 6px;
    border: none;
    background-color: var(--back-3);
    padding: 3px 12px !important;

    .ant-select-selection-placeholder {
      line-height: 30px;
      color: var(--text-1);
      font-weight: bold;
    }

    .ant-select-selection-item {
      line-height: 30px;
      color: var(--text-1);
      font-weight: bold;
      padding-right: 21px !important;
    }
  }
}

:deep(.ant-select-clear) {
  background-color: var(--back-3);
  opacity: 1;
}

:deep(.ant-divider) {
  display: none;
}

:deep(.ant-select-selection-item, .ant-select-selection-placeholder) {
  padding-right: 10px !important;
}
</style>
